import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import auditService from '../../services/auditService';
import { authService } from '../../services/authService';
import Toast from '../../components/Common/Toast';
import { AUDIT_RULE_CATEGORY_LABELS } from '../../utils/auditRuleCatalog';

export default function AuditRulesSettingsPage() {
  const [rules, setRules] = useState([]);
  const [transferDays, setTransferDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const canEdit = useMemo(() => {
    const user = authService.getCurrentUser();
    return Boolean(user && user.role === 'administrador');
  }, []);

  const groupedRules = useMemo(() => {
    const groups = { kardex: [], inventory: [] };
    rules.forEach((rule) => {
      const key = rule.category === 'inventory' ? 'inventory' : 'kardex';
      groups[key].push(rule);
    });
    return groups;
  }, [rules]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await auditService.getRules();
      if (res.success) {
        setRules(res.data?.rules || []);
        const transferRule = (res.data?.rules || []).find((r) => r.code === 'traspaso_sin_recepcion');
        const days = transferRule?.params?.transfer_incomplete_days_threshold;
        if (days != null) setTransferDays(Number(days));
      }
    } catch {
      setToast({ isOpen: true, message: 'Error al cargar reglas', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleRule = (code, enabled) => {
    setRules((prev) => prev.map((rule) => (rule.code === code ? { ...rule, enabled } : rule)));
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await auditService.updateRules({
        rules: rules.map((rule) => ({ code: rule.code, enabled: Boolean(rule.enabled) })),
        transfer_incomplete_days_threshold: transferDays,
      });
      if (res.success) {
        setRules(res.data?.rules || []);
        const transferRule = (res.data?.rules || []).find((r) => r.code === 'traspaso_sin_recepcion');
        const days = transferRule?.params?.transfer_incomplete_days_threshold;
        if (days != null) setTransferDays(Number(days));
        setToast({ isOpen: true, message: 'Reglas guardadas', type: 'success' });
      }
    } catch {
      setToast({ isOpen: true, message: 'No se pudieron guardar las reglas', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <nav className="text-sm text-gray-600 mb-3">
        <Link to="/audit/kardex" className="hover:text-teal-600">Auditoría</Link>
        <span className="mx-2">/</span>
        <span className="text-gray-800">Reglas</span>
      </nav>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Reglas de auditoría</h1>
        <p className="text-gray-600 mt-1 text-sm">
          Active o desactive qué condiciones generan eventos y notificaciones. Los destinatarios se configuran aparte.
        </p>
        <Link to="/audit/inventory-count" className="inline-flex mt-2 text-sm text-teal-700 hover:underline">
          Configurar toma de inventario por almacén →
        </Link>
      </div>

      {!canEdit && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
          Solo un administrador puede modificar las reglas. Puede consultar el listado actual.
        </div>
      )}

      {loading ? (
        <p className="text-gray-500">Cargando…</p>
      ) : (
        <>
          {['kardex', 'inventory'].map((category) => (
            <section key={category} className="mb-6 bg-white border rounded-lg shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b bg-gray-50">
                <h2 className="font-semibold text-gray-800">{AUDIT_RULE_CATEGORY_LABELS[category]}</h2>
                {category === 'inventory' && (
                  <p className="text-xs text-gray-500 mt-1">
                    Plazos y productos por almacén en{' '}
                    <Link to="/audit/inventory-count" className="text-teal-700 hover:underline">Toma de inventario</Link>.
                  </p>
                )}
              </div>
              <ul className="divide-y">
                {groupedRules[category].map((rule) => (
                  <li key={rule.code} className="px-4 py-4 flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900">{rule.label}</p>
                      <p className="text-sm text-gray-500 mt-0.5">{rule.description}</p>
                      {rule.code === 'traspaso_sin_recepcion' && (
                        <label className="block mt-3 text-sm text-gray-700">
                          Días sin recepción para alertar
                          <input
                            type="number"
                            min={1}
                            max={365}
                            className="mt-1 w-24 border rounded px-2 py-1 ml-0 block"
                            value={transferDays}
                            disabled={!canEdit || !rule.enabled}
                            onChange={(e) => setTransferDays(Number(e.target.value))}
                          />
                        </label>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-sm text-gray-500">{rule.enabled ? 'Activa' : 'Inactiva'}</span>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={rule.enabled}
                        disabled={!canEdit}
                        onClick={() => toggleRule(rule.code, !rule.enabled)}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                          rule.enabled ? 'bg-teal-600' : 'bg-gray-200'
                        } ${!canEdit ? 'opacity-60 cursor-not-allowed' : ''}`}
                      >
                        <span
                          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                            rule.enabled ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))}

          {canEdit && (
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="px-4 py-2 bg-teal-600 text-white rounded text-sm disabled:opacity-50"
            >
              {saving ? 'Guardando…' : 'Guardar reglas'}
            </button>
          )}
        </>
      )}

      <Toast {...toast} onClose={() => setToast((t) => ({ ...t, isOpen: false }))} />
    </div>
  );
}
